import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Botao, Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { get } from '../../nucleo/api';
import { dinheiro, mesAno } from '../../nucleo/formato';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, NomeParceiro, percentualTexto, Rolagem, TD, TH, usePeriodo, Valor } from './comum';
import { SeletorIntercompany, useIntercompany } from './Dre';
import type { Margem as MargemApi, TitulosMargem } from './tipos';

type Servico = MargemApi['por_moeda'][number]['servicos'][number];
type Alvo = { servico: Servico; moeda: string };

/**
 * Fase 2 (W21, K13): R8 · Margem por serviço realizada. Receita líquida do desconto do contrato, custo e margem por
 * serviço do catálogo, das cobranças de contrato emitidas com competência no período; drill até as cobranças e as
 * faturas. Mesmos componentes das outras abas (período, escopo de empresa, consolidado, exportação e modo discreto).
 */
export function Margem() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [inter, setInter, interVigente] = useIntercompany();
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const params = { de: periodo.de, ate: periodo.ate, intercompany: inter };
  const q = useQuery({ queryKey: ['/relatorios/margem', params], queryFn: () => get<MargemApi>('/relatorios/margem', params), placeholderData: keepPreviousData });
  const pct = (v: string | null) => percentualTexto(v);
  const nomeServico = (s: Servico) => (s.servico_id ? s.nome : t('margem.semServico', { nome: s.nome }));

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        {q.data?.escopo.modo === 'consolidado' && <SeletorIntercompany valor={interVigente} aoMudar={setInter} />}
        <BotaoExportar relatorio="margem" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <div>
              <h2 className="text-base font-semibold text-tinta-1">{t('margem.titulo')}</h2>
              <LinhaEscopo escopo={d.escopo} />
            </div>
            {!d.disponivel ? (
              <Cartao>
                <Vazio titulo={t('margem.indisponivelTitulo')} texto={t('margem.indisponivelTexto')} />
              </Cartao>
            ) : d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('margem.vazioTitulo')} texto={t('margem.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => (
                <section key={m.moeda} aria-label={t('comum.moeda', { moeda: m.moeda })} className="flex min-w-0 flex-col gap-4">
                  {d.por_moeda.length > 1 && <h2 className="text-sm font-semibold">{t('comum.moeda', { moeda: m.moeda })}</h2>}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Kpi rotulo={t('margem.receita')} valor={dinheiro(m.total.receita_centavos, m.moeda)} />
                    <Kpi rotulo={t('margem.custo')} valor={dinheiro(m.total.custo_centavos, m.moeda)} />
                    <Kpi rotulo={t('margem.margem')} cor={m.total.margem_centavos < 0 ? 'text-perigo' : 'text-verde'} valor={dinheiro(m.total.margem_centavos, m.moeda)} />
                    <Kpi rotulo={t('margem.margemPct')} valor={pct(m.total.margem_pct) || '-'} detalhe={`${t('margem.cobrancas')}: ${m.total.cobrancas}`} />
                  </div>
                  <Cartao titulo={t('margem.servico')} semPadding>
                    <Rolagem rotulo={`${t('comum.tabela')} (${m.moeda})`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-borda text-left text-tinta-3">
                            <th scope="col" className={TH}>{t('margem.servico')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('margem.cobrancas')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('margem.receita')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('margem.custo')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('margem.margem')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('margem.margemPct')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.servicos.map((sv) => {
                            const nome = nomeServico(sv);
                            return (
                              <tr key={sv.servico_id ?? `livre-${sv.nome}`} className="border-b border-borda last:border-0" data-servico={sv.servico_id ?? ''}>
                                <th scope="row" className={`${TD} max-w-72 text-left font-normal break-words`}>
                                  <button type="button" onClick={() => setAlvo({ servico: sv, moeda: m.moeda })} aria-label={t('margem.detalhar', { nome })} className="text-left break-words text-tinta-1 underline-offset-2 hover:text-acento-forte hover:underline">
                                    {nome}
                                  </button>
                                </th>
                                <td className={`${TD} numero text-right`}>{sv.cobrancas}</td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={sv.receita_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={sv.custo_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={sv.margem_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} numero text-right`}>{pct(sv.margem_pct)}</td>
                              </tr>
                            );
                          })}
                          <tr className="border-t border-borda font-semibold">
                            <th scope="row" className={`${TD} text-left`}>{t('comum.total')}</th>
                            <td className={`${TD} numero text-right`}>{m.total.cobrancas}</td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.receita_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.custo_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.margem_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} numero text-right`}>{pct(m.total.margem_pct)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </Rolagem>
                    <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('margem.ajuda')}</p>
                  </Cartao>
                  {d.escopo.modo === 'consolidado' && m.por_empresa.length > 0 && (
                    <Cartao titulo={t('margem.porEmpresa')} semPadding>
                      <Rolagem rotulo={`${t('margem.porEmpresa')} (${m.moeda})`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('margem.empresa')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('margem.receita')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('margem.custo')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('margem.margem')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('margem.margemPct')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.por_empresa.map((e) => (
                              <tr key={e.empresa_id ?? 'sem'} className="border-b border-borda last:border-0">
                                <th scope="row" className={`${TD} text-left font-normal`}>{d.escopo.empresas.find((x) => x.id === e.empresa_id)?.nome ?? ''}</th>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={e.receita_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={e.custo_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={e.margem_centavos} moeda={m.moeda} />
                                </td>
                                <td className={`${TD} numero text-right`}>{pct(e.margem_pct)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Rolagem>
                    </Cartao>
                  )}
                </section>
              ))
            )}
          </>
        )}
      </Estado>
      {alvo && (
        <DetalheMargem
          titulo={nomeServico(alvo.servico)}
          params={{ ...params, moeda: alvo.moeda, ...(alvo.servico.servico_id ? { servico_id: alvo.servico.servico_id } : { descricao: alvo.servico.nome }) }}
          aoFechar={() => setAlvo(null)}
        />
      )}
    </div>
  );
}

/** Drill da margem: as cobranças do serviço (cada uma com a fatura emitida), com a soma do filtro inteiro do servidor. */
function DetalheMargem({ titulo, params, aoFechar }: { titulo: string; params: Record<string, unknown>; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: ['/relatorios/margem/titulos', params], queryFn: () => get<TitulosMargem>('/relatorios/margem/titulos', { ...params, limite: 500 }) });
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('margem.detalheTitulo', { nome: titulo })}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="text-sm text-tinta-3">{t('margem.detalheVazio')}</p>
          ) : (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-sm text-tinta-2">
                {d.soma_por_moeda.map((s) => (
                  <span key={s.moeda} className="mr-3 block">
                    {t('margem.soma', { receita: dinheiro(s.receita_centavos, s.moeda), custo: dinheiro(s.custo_centavos, s.moeda), margem: dinheiro(s.margem_centavos, s.moeda) })}
                  </span>
                ))}
              </p>
              <div tabIndex={0} role="region" aria-label={t('comum.tabela')} className="max-h-96 overflow-auto rounded-lg border border-borda focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-sup-1">
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-3 py-2 font-medium">{t('margem.competencia')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('margem.contrato')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('margem.receita')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('margem.custo')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('margem.margem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.itens.map((i) => (
                      <tr key={i.cobranca_id} className="border-b border-borda last:border-0">
                        <td className="numero px-3 py-2 whitespace-nowrap text-tinta-2">{mesAno(i.competencia.slice(0, 7))}</td>
                        <td className="px-3 py-2">
                          <span className="block font-mono text-xs text-tinta-1">{i.contrato_numero}</span>
                          <span className="block text-xs break-words text-tinta-3">
                            <NomeParceiro nome={i.parceiro_nome} vazio={t('comum.semNome')} />
                            {i.fatura_id && (
                              <>
                                {' · '}
                                <Link to={`/financeiro/contas/a-receber?estado=todos&parceiro_id=${i.parceiro_id}&de=${i.vencimento}&ate=${i.vencimento}`} className="text-acento-forte underline-offset-2 hover:underline">
                                  {t('margem.abrirFatura')}
                                </Link>
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Valor centavos={i.receita_centavos} moeda={i.moeda} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Valor centavos={i.custo_centavos} moeda={i.moeda} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Valor centavos={i.margem_centavos} moeda={i.moeda} />
                          <span className="numero block text-xs text-tinta-3">{percentualTexto(i.margem_pct)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {d.truncado && <p className="text-xs text-tinta-3">{t('margem.mostrando', { n: d.itens.length, total: d.total })}</p>}
            </div>
          )
        }
      </Estado>
    </Modal>
  );
}
